<!-- Read-only detail popup for a Storage item (uploaded file). Shows the slide preview +
     label, technical image information, and Case Information (when the slide has been added
     to a project as an ImageInstance — case-info lives on the instance, not on storage).
     Unlike ImageDetailModal this is project-context-free and non-editable, so it works on the
     storage page where there is no currentProject. The parent resolves the AbstractImage /
     ImageInstance and passes it in as `image`. -->
<template>
<b-modal :active="active" has-modal-card scroll="keep" @update:active="v => $emit('update:active', v)">
  <div class="modal-card sdm-card">
    <header class="modal-card-head sdm-head">
      <span class="sdm-title">{{ titleName }}</span>
      <div class="sdm-tools">
        <router-link v-if="viewerRoute" :to="viewerRoute" class="button is-link is-small" @click.native="close">
          {{ $t('view-image') }}
        </router-link>
        <button class="delete" :aria-label="$t('button-close')" @click="close"></button>
      </div>
    </header>

    <section class="modal-card-body sdm-body">
      <div v-if="loading" class="sdm-loading"><b-loading :is-full-page="false" :active="true" /></div>

      <div v-else-if="!image" class="sdm-empty">
        <em class="sdm-muted">{{ $t('slide-preview-not-available') }}</em>
      </div>

      <div v-else class="sdm-grid">
        <aside class="sdm-side">
          <div class="sdm-media">
            <a v-if="hasMacro" class="sdm-big" @click="openZoom('macro')" :title="$t('slide-preview')">
              <image-thumbnail :image="image" :macro="true" :size="640" :key="`${image.id}-macro`" :extra-parameters="tokenParam" />
              <span class="zoom-badge"><i class="fas fa-expand"></i></span>
            </a>
            <em v-else class="sdm-muted">{{ $t('slide-preview-not-available') }}</em>

            <div v-if="hasMacro && hasLabel" class="sdm-label">
              <span class="sdm-cap">{{ $t('slide-label') }}</span>
              <a class="sdm-label-body" @click="openZoom('label')" :title="$t('slide-label')">
                <image-thumbnail :image="image" :label="true" :size="320" :key="`${image.id}-label`" :extra-parameters="tokenParam" @error="hasLabel = false" />
                <span class="zoom-badge corner"><i class="fas fa-expand"></i></span>
              </a>
            </div>

            <div class="sdm-fn">
              <span class="sdm-cap">{{ $t('originalFilename') }}</span>
              <b>{{ titleName }}</b>
            </div>

            <dl class="sdm-meta">
              <div class="sdm-meta-row">
                <dt>{{ $t('format') }}</dt>
                <dd>{{ image.contentType || $t('unknown') }}</dd>
              </div>
              <div class="sdm-meta-row">
                <dt>{{ $t('image-width') }}</dt>
                <dd>{{ image.width }} {{ $t('pixels') }}</dd>
              </div>
              <div class="sdm-meta-row">
                <dt>{{ $t('image-height') }}</dt>
                <dd>{{ image.height }} {{ $t('pixels') }}</dd>
              </div>
              <div class="sdm-meta-row">
                <dt>{{ $t('x-y-resolution') }}</dt>
                <dd>
                  <template v-if="image.physicalSizeX">{{ image.physicalSizeX.toFixed(3) }} {{ $t('um-per-pixel') }}</template>
                  <template v-else>{{ $t('unknown') }}</template>
                </dd>
              </div>
              <div class="sdm-meta-row">
                <dt>{{ $t('magnification') }}</dt>
                <dd>
                  <template v-if="image.magnification">{{ image.magnification }}</template>
                  <template v-else>{{ $t('unknown') }}</template>
                </dd>
              </div>
            </dl>
          </div>
        </aside>

        <div class="sdm-main">
          <h3 class="sdm-section-title">{{ $t('case-information-title') }}</h3>
          <case-information-panel :image="image" />
        </div>
      </div>
    </section>
  </div>

  <div v-if="zoom" class="zoomscrim" @click.self="zoom = null">
    <div class="zoombox">
      <div class="zb-head">{{ zoom === 'label' ? $t('slide-label') : $t('slide-preview') }}</div>
      <div class="zb-body" :class="{label: zoom === 'label'}">
        <img :src="zoomSrc" alt="">
      </div>
      <div class="zb-foot">
        <router-link v-if="zoom === 'macro' && viewerRoute" :to="viewerRoute" class="button is-link" @click.native="close">{{ $t('view-image') }}</router-link>
        <button class="button" @click="zoom = null">{{ $t('button-close') }}</button>
      </div>
    </div>
  </div>
</b-modal>
</template>

<script>
import {get} from '@/utils/store-helpers';
import {appendShortTermToken} from '@/utils/token-utils.js';
import CaseInformationPanel from '@/components/image/CaseInformationPanel';
import ImageThumbnail from '@/components/image/ImageThumbnail';

export default {
  name: 'storage-detail-modal',
  components: {CaseInformationPanel, ImageThumbnail},
  props: {
    active: {type: Boolean, default: false},
    image: {type: Object, default: null},
    loading: {type: Boolean, default: false},
    fileName: {type: String, default: ''}
  },
  data() {
    return {
      hasLabel: true,
      zoom: null // 'macro' | 'label' | null
    };
  },
  computed: {
    shortTermToken: get('currentUser/shortTermToken'),
    titleName() {
      return this.fileName || (this.image && (this.image.originalFilename || this.image.instanceFilename)) || '';
    },
    hasMacro() {
      return !!(this.image && this.image.macroURL);
    },
    tokenParam() {
      return {authorization: 'Bearer ' + this.shortTermToken};
    },
    viewerRoute() {
      // only available when the slide is a project image instance
      return (this.image && this.image.project) ? `/project/${this.image.project}/image/${this.image.id}` : '';
    },
    zoomSrc() {
      if (!this.image || !this.zoom) {
        return '';
      }
      return appendShortTermToken(this.image.associatedImageURL(this.zoom, 1024, 'jpg'), this.shortTermToken);
    }
  },
  watch: {
    image() {
      this.hasLabel = true;
      this.zoom = null;
    }
  },
  methods: {
    close() {
      this.$emit('update:active', false);
    },
    openZoom(kind) {
      this.zoom = kind;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/colors.scss';

.sdm-card {
  width: 860px;
  max-width: calc(100vw - 40px);
  border-radius: 12px;
  overflow: hidden;
}
.sdm-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1.3rem;
  background: #fff;
  border-bottom: 1px solid $line-1;
}
.sdm-title {
  font-weight: 700;
  font-size: 1.05rem;
  color: $ink-1;
  min-width: 0;
  word-break: break-all;
}
.sdm-tools { display: flex; align-items: center; gap: 0.9rem; flex: 0 0 auto; }
.sdm-body { padding: 0; min-height: 8rem; position: relative; }
.sdm-loading { min-height: 12rem; position: relative; }
.sdm-empty { padding: 2rem 1.3rem; }
.sdm-muted { color: #8a91a1; font-style: italic; }

.sdm-grid {
  display: grid;
  grid-template-columns: 340px 1fr;
  @media screen and (max-width: 768px) { grid-template-columns: 1fr; }
}
.sdm-side {
  padding: 1.2rem 1.25rem;
  border-right: 1px solid $line-1;
  @media screen and (max-width: 768px) { border-right: 0; border-bottom: 1px solid $line-1; }
}
.sdm-media { display: flex; flex-direction: column; gap: 0.9rem; }
.sdm-big {
  position: relative;
  width: 100%;
  min-height: 150px;
  border: 1px solid $line-1;
  border-radius: 10px;
  overflow: hidden;
  background: #f4f5f7;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-in;
  ::v-deep .image-thumbnail { width: 100%; display: block; }
  &:hover .zoom-badge { background: $primary; }
}
.sdm-label { border: 1px solid $line-1; border-radius: 10px; padding: 0.8rem 0.85rem; background: #fff; }
.sdm-label-body {
  position: relative;
  display: flex; align-items: center; justify-content: center; min-height: 110px;
  cursor: zoom-in;
  ::v-deep .image-thumbnail { max-width: 100%; max-height: 180px; display: block; }
  &:hover .zoom-badge { background: $primary; }
}
.zoom-badge {
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(38, 42, 49, 0.82);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sdm-cap {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8a91a1;
  margin-bottom: 0.5rem;
}
.sdm-fn { font-size: 0.85rem; color: $ink-1; }
.sdm-fn b { display: block; font-weight: 600; word-break: break-all; margin-top: 0.15rem; }
.sdm-meta { margin: 0; border: 1px solid $line-1; border-radius: 10px; overflow: hidden; }
.sdm-meta-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 0.75rem;
  align-items: baseline;
  padding: 0.5rem 0.85rem;
  font-size: 0.83rem;
  & + .sdm-meta-row { border-top: 1px solid $line-1; }
  dt { font-weight: 600; color: $ink-1; }
  dd { margin: 0; color: $ink-3; word-break: break-word; }
}
.sdm-main { padding: 1.1rem 1.3rem 1.3rem; min-width: 0; }
.sdm-section-title {
  font-size: 1rem;
  font-weight: 700;
  color: $ink-1;
  margin-bottom: 0.8rem;
  padding-bottom: 0.45rem;
  border-bottom: 1px solid $line-1;
}

/* zoom lightbox (mirrors ImageDetailModal) */
.zoomscrim {
  position: fixed;
  inset: 0;
  background: rgba(12, 14, 18, 0.78);
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 36px;
}
.zoombox {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.4);
  max-width: 680px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  max-height: 90vh;
}
.zb-head {
  font-size: 0.85rem;
  font-weight: 600;
  color: $ink-3;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.zb-body {
  border: 1px solid $line-1;
  border-radius: 10px;
  overflow: auto;
  background: #f4f5f7;
  text-align: center;
  img { max-width: 100%; display: inline-block; }
  &.label { padding: 24px; background: #fff; }
}
.zb-foot {
  display: flex;
  justify-content: center;
  gap: 0.8rem;
}
</style>
